import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, Check, ArrowUpRight, Upload, Layers } from "lucide-react";
import { toast } from "sonner";
import { generateArticle, generateBatch, createArticle, uploadImage, mediaUrl } from "@/lib/api";

const CATS = ["music", "athletes", "fashion", "shoes", "cars", "travel", "technology", "business", "media", "food"];

export default function Studio() {
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("music");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [batchTopics, setBatchTopics] = useState("");
  const [batchCat, setBatchCat] = useState("music");
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResults, setBatchResults] = useState(null);

  const handleBatch = async () => {
    const topics = batchTopics.split("\n").map((t) => t.trim()).filter(Boolean).slice(0, 5);
    if (topics.length === 0) {
      toast.error("Enter at least one topic (one per line).");
      return;
    }
    setBatchLoading(true);
    setBatchResults(null);
    try {
      const data = await generateBatch(topics, batchCat);
      setBatchResults(data.results);
      toast.success(`Published ${data.published} of ${topics.length} articles.`);
    } catch (e) {
      toast.error("Batch generation failed. Try fewer topics.");
    } finally {
      setBatchLoading(false);
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !result) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setResult((r) => ({ ...r, image: url }));
      toast.success("Your image is now on this story.");
    } catch {
      toast.error("Upload failed (max 8MB).");
    } finally {
      setUploading(false);
    }
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error("Give the editor a topic first.");
      return;
    }
    setLoading(true);
    setResult(null);
    setPublished(null);
    try {
      const data = await generateArticle(topic, category);
      setResult(data);
      toast.success("Feature generated.");
    } catch (e) {
      toast.error("Generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!result) return;
    setPublishing(true);
    try {
      const saved = await createArticle({
        title: result.title,
        dek: result.dek,
        body: result.body,
        category: result.category,
        image: result.image,
      });
      setPublished(saved);
      toast.success(`Published to ${result.category}.`);
    } catch (e) {
      toast.error("Publish failed.");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-10 py-16">
      <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">NS Studio</span>
      <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight mt-3 leading-none">
        The Editor's Desk
      </h1>
      <p className="text-muted-foreground text-lg mt-4 max-w-2xl">
        Pitch a topic. Our AI editor drafts a NERO SUN feature in the magazine's
        voice — instantly.
      </p>
      <Link
        to="/studio/advertorial"
        data-testid="studio-advertorial-link"
        className="inline-flex items-center gap-2 mt-5 font-mono text-xs uppercase tracking-widest text-foreground/80 border border-white/15 px-4 py-2 hover:border-signal hover:text-signal transition-colors"
      >
        Building a sponsored story? Open the Advertorial Builder →
      </Link>

      <div className="border border-white/10 bg-card p-6 md:p-8 mt-10 space-y-5">
        <div>
          <label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Topic / Pitch</label>
          <input
            data-testid="studio-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
            placeholder="e.g. The rise of luxury sneaker investing"
            className="w-full mt-2 bg-background border border-white/15 px-4 py-3 text-foreground placeholder:text-muted-foreground/60 focus:border-signal outline-none font-sans"
          />
        </div>

        <div>
          <label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Section</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`studio-cat-${c}`}
                onClick={() => setCategory(c)}
                className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                  category === c
                    ? "bg-signal text-white border-signal"
                    : "border-white/15 text-foreground/70 hover:border-signal"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <button
          data-testid="studio-generate-btn"
          onClick={handleGenerate}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-white text-black font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Drafting…" : "Generate Feature"}
        </button>
      </div>

      {/* BATCH MODE */}
      <div className="border border-signal/30 bg-card p-6 md:p-8 mt-8 space-y-5" data-testid="studio-batch">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-signal" />
          <div className="font-anton text-xl uppercase tracking-tight">Batch Generate — up to 5 at once</div>
        </div>
        <p className="text-muted-foreground text-sm">
          Pump out content fast. Enter up to 5 topics (one per line). Each is written and
          <b className="text-foreground/80"> auto-published</b> to the section below — great for growing your indexed pages.
        </p>
        <div>
          <label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Topics (one per line)</label>
          <textarea
            data-testid="studio-batch-topics"
            value={batchTopics}
            onChange={(e) => setBatchTopics(e.target.value)}
            rows={5}
            placeholder={"The rise of Afrobeats globally\nBest chef-owned restaurants in Atlanta\nSneaker resale market in 2026"}
            className="w-full mt-2 bg-background border border-white/15 px-4 py-3 text-foreground text-sm placeholder:text-muted-foreground/60 focus:border-signal outline-none font-sans"
          />
        </div>
        <div>
          <label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Section</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`studio-batch-cat-${c}`}
                onClick={() => setBatchCat(c)}
                className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                  batchCat === c ? "bg-signal text-white border-signal" : "border-white/15 text-foreground/70 hover:border-signal"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        <button
          data-testid="studio-batch-btn"
          onClick={handleBatch}
          disabled={batchLoading}
          className="inline-flex items-center gap-2 bg-signal text-white font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-signal-hover transition-colors disabled:opacity-50"
        >
          {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
          {batchLoading ? "Writing & publishing…" : "Generate & Publish Batch"}
        </button>

        {batchResults && (
          <div className="border-t border-white/10 pt-4 space-y-2" data-testid="studio-batch-results">
            {batchResults.map((r, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                {r.ok ? (
                  <>
                    <Check className="w-4 h-4 text-rankup shrink-0" />
                    <span className="text-foreground/85 truncate">{r.title}</span>
                    <Link to={`/article/${r.slug}`} className="font-mono text-[10px] uppercase tracking-widest text-signal hover:underline shrink-0">View →</Link>
                  </>
                ) : (
                  <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">✕ {r.topic} — failed</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {result && (
        <article className="mt-12 animate-rise" data-testid="studio-result">
          <div className="relative h-72 overflow-hidden border border-white/10">
            <img src={mediaUrl(result.image)} alt={result.title} className="w-full h-full object-cover img-treatment" />
            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
            <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-signal text-white px-2 py-1">
              {result.category} · AI
            </span>
          </div>
          {!published && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 cursor-pointer font-mono text-[11px] uppercase tracking-widest border border-white/15 px-4 py-2 hover:border-signal hover:text-signal transition-colors" data-testid="studio-upload-label">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? "Uploading…" : "Use your own image instead"}
                <input type="file" accept="image/*" onChange={handleUploadImage} className="hidden" data-testid="studio-image-input" />
              </label>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60">
                Pick a photo that better matches the story (max 8MB)
              </span>
            </div>
          )}
          <h2 className="font-display font-semibold tracking-tighter text-4xl md:text-5xl mt-6 leading-[0.95]" data-testid="studio-result-title">
            {result.title}
          </h2>
          <p className="font-display text-xl text-foreground/90 mt-4">{result.dek}</p>
          <div className="space-y-5 mt-8">
            {(result.body || "").split("\n\n").map((p, i) => (
              <p key={i} className="text-foreground/85 text-lg leading-relaxed">{p}</p>
            ))}
          </div>

          <div className="mt-10 border-t border-white/10 pt-6">
            {published ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="studio-published">
                <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-rankup">
                  <Check className="w-4 h-4" /> Published to {published.category}
                </span>
                <Link to={`/article/${published.slug}`} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-signal hover:underline">
                  View live story <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to={`/c/${published.category}`} className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-signal">
                  Go to {published.category} section →
                </Link>
              </div>
            ) : (
              <button
                data-testid="studio-publish-btn"
                onClick={handlePublish}
                disabled={publishing}
                className="inline-flex items-center gap-2 bg-signal text-white font-mono text-xs uppercase tracking-widest px-6 py-3 hover:bg-signal-hover transition-colors disabled:opacity-50"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {publishing ? "Publishing…" : `Publish to ${result.category} section`}
              </button>
            )}
          </div>
        </article>
      )}
    </div>
  );
}
